import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from '@reach/router'
import { absoluteUrl, EXCLUDED_LANGUAGES, normalizePath } from "../../lib/urls"

// Fallback order for x-default when a page group has no English version. The
// point is that the choice depends only on the group, never on which member is
// rendering — otherwise every language names itself and the cluster ends up
// with as many x-defaults as it has pages.
const X_DEFAULT_FALLBACK_ORDER = ["fr", "es", "pt", "ar", "sw"]

const pickXDefaultLang = (codes, defaultLang) => {
  if (codes.includes(defaultLang)) return defaultLang
  return (
    X_DEFAULT_FALLBACK_ORDER.find(code => codes.includes(code)) ||
    [...codes].sort()[0]
  )
}

const HrefLangs = ({ languages, slug = '/', lang, noAlternates = false }) => {
  const { pathname } = useLocation();

  const {
     site: {
      siteMetadata: { siteUrl, default_lang },
    },
    } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
            default_lang
          }
        }
      }
    `
  );

  // The canonical is the page's own URL. Everything else — including this
  // page's self-referencing alternate — is measured against this exact string,
  // which is what "no self-referencing hreflang" is really testing.
  const canonical = absoluteUrl(siteUrl, pathname)

  const currentLang = lang || default_lang

  const availableLanguages = languages?.filter(
    language =>
      !EXCLUDED_LANGUAGES.includes(language?.node?.fmmCore?.languageCode)
  ) || [];

  const hrefForLanguage = languageCode =>
    languageCode === currentLang
      ? canonical
      : absoluteUrl(
          siteUrl,
          `${languageCode === default_lang ? '' : `/${languageCode}`}${
            normalizePath(slug) === '/' ? '' : normalizePath(slug)
          }`
        )

  // Paginated series (/blog/2 …) have no per-language equivalent — the other
  // languages have different post counts — so they claim no cross-language
  // alternates. They still annotate themselves: dropping the block entirely is
  // what "no self-referencing hreflang" flags in the first place. x-default is
  // only emitted by the default-language page, since a non-default page is not
  // the fallback for unmatched languages.
  if (noAlternates) {
    return (
      <Helmet>
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hreflang={currentLang} href={canonical} />
        {currentLang === default_lang && (
          <link rel="alternate" hreflang="x-default" href={canonical} />
        )}
      </Helmet>
    )
  }

  // Deduped: two WordPress nodes sharing a language code would otherwise emit
  // the same hreflang twice, which is itself a conflict.
  const alternates = [
    ...new Set(
      [...availableLanguages]
        .reverse()
        .map(language => language?.node?.fmmCore?.languageCode)
        .filter(Boolean)
    ),
  ]

  // The page must always annotate itself, even when its own language is missing
  // from the WordPress result set.
  if (!alternates.includes(currentLang)) {
    alternates.unshift(currentLang)
  }

  const xDefaultHref = hrefForLanguage(pickXDefaultLang(alternates, default_lang))

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hreflang="x-default" href={xDefaultHref} />

      {alternates.map(languageCode => (
        <link
          rel="alternate"
          key={languageCode}
          hreflang={languageCode}
          href={hrefForLanguage(languageCode)}
        />
      ))}
    </Helmet>
  )
}

export default HrefLangs
